import React, { Fragment, useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
  colors,
  Badge,
} from '@mui/material';
import { CRQuestion } from 'views/App/Portal/Policy/_types/crq';
import { translate as t } from 'hooks/useTranslations';
import { Edit, InfoOutlined } from '@mui/icons-material';

interface QuestionItemProps {
  answerable: boolean;
  answerQuestion: (questionId: string, value: string) => void;
  editable: boolean;
  question: CRQuestion;
  setEditQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
}

const QuestionItem: React.FC<QuestionItemProps> = ({
  answerable,
  answerQuestion,
  editable,
  question,
  setEditQuestionId,
}) => {
  const [currentAnswer, setCurrentAnswer] = useState<string>(question.answer ?? '');
  useEffect(() => {
    if (currentAnswer && currentAnswer !== question.answer) {
      answerQuestion(question.id!, currentAnswer);
    }
  }, [currentAnswer, question.answer, question.id]);

  return (
    <Card
      className='phx-card'
      id={`question_${question.id}`}
      sx={{
        '.edit-btn': { visibility: 'hidden' },
        '&:hover .edit-btn': { visibility: 'visible' },
      }}
    >
      <Stack spacing={4}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 4,
            alignItems: 'end',
          }}
        >
          <Typography variant='subtitle1'>{question.question}</Typography>
          <Box>
            {editable && (
              <IconButton
                className='edit-btn'
                onClick={() => setEditQuestionId(question.id!)}
              >
                <Edit />
              </IconButton>
            )}
          </Box>
        </Box>
        {answerable ? (
          <>
            <RadioGroup
              defaultValue={question.answer}
              id={`question_${question.id}_answer`}
              key={`question_${question.id}_answer`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCurrentAnswer((event.target as HTMLInputElement).value);
              }}
              sx={{ pl: 5 }}
            >
              {question.choices?.map((choice) => (
                <Fragment key={`question_${question.id}_${choice.value}`}>
                  <FormControlLabel
                    value={choice.value}
                    control={<Radio />}
                    label={choice.value}
                  />
                </Fragment>
              ))}
            </RadioGroup>
          </>
        ) : (
          <>
            {question.choices && (
              <Stack
                spacing={2}
                sx={{ pl: 5 }}
              >
                {question.choices.map((choice, index) => (
                  <Stack
                    key={`choice_${question.id}_${index}`}
                    direction='row'
                    spacing={2}
                  >
                    <Typography>{choice.value}</Typography>
                    <Badge
                      variant='inline'
                      badgeContent={`${choice.content?.length ?? 0}`}
                      color='neutral'
                      sx={{ textTransform: 'uppercase' }}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        )}
        {question.hint && question.hint.length > 0 && (
          <Typography
            variant='caption'
            color='textSecondary'
            style={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <InfoOutlined sx={{ fontSize: '0.85rem' }} />
            {question.hint}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

export default QuestionItem;
